import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Filter, SortAsc, SortDesc, Search } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { format } from 'date-fns';

export function CaseHistory() {
  const navigate = useNavigate();
  const [cases, setCases] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('last_modified');
  const [sortDirection, setSortDirection] = useState('desc');
  const [hideClosed, setHideClosed] = useState(true);
  const [insuranceFilter, setInsuranceFilter] = useState('');
  const [insuranceCompanies, setInsuranceCompanies] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCases();
    fetchInsuranceCompanies();
  }, [sortField, sortDirection, hideClosed, insuranceFilter]);

  const fetchCases = async () => {
    try {
      let query = supabase
        .from('cases')
        .select(`
          *,
          bike:bikes(
            id,
            model,
            rego_number
          )
        `);

      // Apply closed/open filter
      if (hideClosed) {
        query = query.neq('status', 'closed');
      }

      // Apply insurance company filter
      if (insuranceFilter) {
        query = query.eq('not_at_fault_insurance_company', insuranceFilter);
      }

      // Apply sorting
      if (sortField === 'at_fault_rego') {
        query = query.order('at_fault_rego', { ascending: sortDirection === 'asc' });
      } else if (sortField === 'case_number') {
        query = query.order('case_number', { ascending: sortDirection === 'asc' });
      } else if (sortField === 'client') {
        query = query.order('not_at_fault_name', { ascending: sortDirection === 'asc' });
      } else if (sortField === 'insurance') {
        query = query.order('not_at_fault_insurance_company', { ascending: sortDirection === 'asc' });
      } else if (sortField === 'status') {
        query = query.order('status', { ascending: sortDirection === 'asc' });
      } else if (sortField === 'last_modified') {
        query = query.order('last_modified', { ascending: sortDirection === 'asc' });
      } else if (sortField === 'created_at') {
        query = query.order('created_at', { ascending: sortDirection === 'asc' });
      }

      const { data, error } = await query;

      if (error) throw error;
      setCases(data || []);
    } catch (error) {
      console.error('Error fetching cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInsuranceCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('cases')
        .select('not_at_fault_insurance_company')
        .not('not_at_fault_insurance_company', 'is', null);

      if (error) throw error;

      const uniqueCompanies = [...new Set(data.map(c => c.not_at_fault_insurance_company))];
      setInsuranceCompanies(uniqueCompanies.sort());
    } catch (error) {
      console.error('Error fetching insurance companies:', error);
    }
  };

  const toggleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const filteredCases = cases.filter(case_ => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      case_.at_fault_rego?.toLowerCase().includes(searchLower) ||
      case_.not_at_fault_name?.toLowerCase().includes(searchLower) ||
      case_.not_at_fault_insurance_company?.toLowerCase().includes(searchLower) ||
      case_.claim_number?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  const SortIndicator = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    
    return (
      <span className="inline-flex items-center ml-1">
        {sortDirection === 'asc' ? (
          <SortAsc className="h-4 w-4" />
        ) : (
          <SortDesc className="h-4 w-4" />
        )}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-blue-500 bg-clip-text text-transparent">
          Case History
        </h1>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="relative w-full md:w-auto">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search cases..."
              className="w-full md:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          </div>
          <select
            value={insuranceFilter}
            onChange={(e) => setInsuranceFilter(e.target.value)}
            className="w-full md:w-auto px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">All Insurance Companies</option>
            {insuranceCompanies.map(company => (
              <option key={company} value={company}>{company}</option>
            ))}
          </select>
          <button
            onClick={() => setHideClosed(!hideClosed)}
            className={`w-full md:w-auto px-4 py-2 rounded-lg border ${
              hideClosed 
                ? 'bg-indigo-50 text-indigo-600 border-indigo-200' 
                : 'bg-gray-50 text-gray-600 border-gray-200'
            }`}
          >
            <Filter className="h-4 w-4 inline-block mr-2" />
            {hideClosed ? 'Show Closed' : 'Hide Closed'}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg overflow-x-auto">
        <table className="min-w-full">
          <thead>
            <tr>
              <th 
                onClick={() => toggleSort('at_fault_rego')}
                className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900 transition-colors"
              >
                REGO
                <SortIndicator field="at_fault_rego" />
              </th>
              <th 
                onClick={() => toggleSort('case_number')}
                className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900 transition-colors"
              >
                Case Number
                <SortIndicator field="case_number" />
              </th>
              <th 
                onClick={() => toggleSort('client')}
                className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900 transition-colors"
              >
                Client
                <SortIndicator field="client" />
              </th>
              <th 
                onClick={() => toggleSort('insurance')}
                className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900 transition-colors"
              >
                Insurance
                <SortIndicator field="insurance" />
              </th>
              <th 
                onClick={() => toggleSort('status')}
                className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900 transition-colors"
              >
                Status
                <SortIndicator field="status" />
              </th>
              <th 
                onClick={() => toggleSort('last_modified')}
                className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900 transition-colors"
              >
                Last Modified
                <SortIndicator field="last_modified" />
              </th>
              <th 
                onClick={() => toggleSort('created_at')}
                className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-900 transition-colors"
              >
                Created
                <SortIndicator field="created_at" />
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredCases.map(case_ => (
              <tr 
                key={case_.id} 
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/cases/${case_.id}`)}
              >
                <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {case_.at_fault_rego || 'N/A'}
                  </div>
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {case_.case_number || `CASE-${case_.id.substring(0, 6)}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    {case_.claim_number}
                  </div>
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{case_.not_at_fault_name}</div>
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{case_.not_at_fault_insurance_company}</div>
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(case_.status)}`}>
                    {formatStatus(case_.status)}
                  </span>
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {format(new Date(case_.last_modified), 'dd MMM yyyy HH:mm')}
                </td>
                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {format(new Date(case_.created_at), 'dd MMM yyyy')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredCases.length === 0 && (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No cases found</h3>
            <p className="text-gray-500">Try adjusting your filters or search terms.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    new_matter: 'bg-gray-100 text-gray-800',
    customer_contacted: 'bg-blue-100 text-blue-800',
    awaiting_approval: 'bg-yellow-100 text-yellow-800',
    bike_delivered: 'bg-green-100 text-green-800',
    bike_returned: 'bg-purple-100 text-purple-800',
    demands_sent: 'bg-indigo-100 text-indigo-800',
    awaiting_settlement: 'bg-orange-100 text-orange-800',
    settlement_agreed: 'bg-emerald-100 text-emerald-800',
    paid: 'bg-teal-100 text-teal-800',
    closed: 'bg-gray-100 text-gray-800'
  };
  return colors[status] || colors.new_matter;
}

function formatStatus(status: string): string {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}